import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { ConsultaList } from 'src/app/shared/model/core/ConsultaList.model';
import { Denuncia, EstatusDenuncia } from 'src/app/shared/model/denuncias/denuncia.model';
import { DenunciaDashboard, DenunciaGrafica } from 'src/app/shared/model/denuncias/denuncia-dashboard.model';
import { CategoriaDenuncia } from 'src/app/shared/model/denuncias/categoria-denuncia.model';
import { DenunciasService } from '../denuncias.service';

@Component({
  selector: 'app-dashboard-denuncias',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './dashboard-denuncias.component.html',
  styleUrls: ['./dashboard-denuncias.component.css']
})
export class DashboardDenunciasComponent implements OnInit {

  readonly EstatusDenuncia = EstatusDenuncia;

  dashboard: DenunciaDashboard = new DenunciaDashboard();
  denuncias: Denuncia[] = [];
  categorias: CategoriaDenuncia[] = [];
  cargando: boolean = false;

  // Filtros
  filtro: ConsultaList<Denuncia> = new ConsultaList<Denuncia>();
  periodoSeleccionado: string = '30';
  fechaInicio: string = '';
  fechaFin: string = '';
  id_categoria: number = null;
  id_estatus: number = null;
  vistaSeleccionada: 'tabla' | 'kanban' = 'tabla';

  // Paginación de "Reportes Recientes" (client-side)
  tamPaginaTabla: number = 10;
  paginaTabla: number = 1;
  tamanosPagina: number[] = [10, 25, 50];

  // Filtros de la tabla (client-side)
  fId: string = '';
  fFecha: string = '';
  fCategoria: string = '';
  fEstado: number | null = null;
  fPrioridad: string | null = null;

  periodoList = [
    { id: '30', nombre: 'Últimos 30 días' },
    { id: '90', nombre: 'Últimos 90 días' },
    { id: '365', nombre: 'Últimos 12 meses' },
    { id: 'all', nombre: 'Todo el historial' }
  ];

  estatusList = [
    { id: 1, nombre: 'Recibidas' },
    { id: 2, nombre: 'En investigación' },
    { id: 3, nombre: 'Cerradas' }
  ];

  constructor(
    private denunciasService: DenunciasService,
    private router: Router,
    private cdr: ChangeDetectorRef
  ) {}

  ngOnInit(): void {
    this.cargarCategorias();
    this.aplicarPeriodoSeleccionado();
    this.aplicarFiltros();
  }

  cargarCategorias(): void {
    this.denunciasService.consultarCategorias().subscribe(resp => {
      if (resp?.list) { this.categorias = resp.list; }
    });
  }

  aplicarFiltros(): void {
    this.cargando = true;
    this.denuncias = [];
    this.dashboard = new DenunciaDashboard();
    this.syncView();

    const req = new ConsultaList<Denuncia>();
    if (this.fechaInicio) { req.fecha_ini = this.construirFechaLocal(this.fechaInicio, false); }
    if (this.fechaFin)    { req.fecha_fin = this.construirFechaLocal(this.fechaFin, true); }
    if (this.id_categoria) { req.param4 = this.id_categoria; }
    if (this.id_estatus)   { req.param5 = this.mapearFiltroEstatus(this.id_estatus); }

    this.denunciasService.consultarDashboard(req).subscribe(
      resp => {
        this.cargando = false;
        this.dashboard = resp || new DenunciaDashboard();
        this.syncView();
      },
      () => {
        this.cargando = false;
        this.dashboard = new DenunciaDashboard();
        this.syncView();
      }
    );

    this.denunciasService.consultarDenunciasTabla(req).subscribe(
      resp => {
        this.denuncias = resp?.list || [];
        this.paginaTabla = 1;
        this.syncView();
      },
      () => {
        this.denuncias = [];
        this.syncView();
      }
    );
  }

  limpiarFiltros(): void {
    this.periodoSeleccionado = '30';
    this.aplicarPeriodoSeleccionado();
    this.id_categoria = null;
    this.id_estatus   = null;
    this.aplicarFiltros();
  }

  onPeriodoChange(): void {
    this.aplicarPeriodoSeleccionado();
    this.aplicarFiltros();
  }

  onCategoriaChange(): void {
    this.aplicarFiltros();
  }

  onEstatusChange(): void {
    this.aplicarFiltros();
  }

  irKanban(): void {
    this.router.navigate(['/DENUNCIAS/kanban']);
  }

  irConfiguracion(): void {
    this.router.navigate(['/DENUNCIAS/configuracion']);
  }

  verDetalle(denuncia: Denuncia): void {
    this.router.navigate(['/DENUNCIAS/detalle', denuncia.id_denuncia]);
  }

  getBadgeClass(id_estatus: number): string {
    const map = { 1: 'estatus-recibida', 2: 'estatus-investigacion', 3: 'estatus-cerrada' };
    return map[id_estatus] || '';
  }

  getPriorityClass(denuncia: Denuncia): string {
    return `prioridad-${this.getPriorityLabel(denuncia).toLowerCase()}`;
  }

  getPriorityLabel(denuncia: Denuncia): string {
    if (denuncia.id_estatus === EstatusDenuncia.EN_INVESTIGACION) {
      return 'Alta';
    }

    if (denuncia.id_estatus === EstatusDenuncia.RECIBIDA) {
      return denuncia.tipoDenuncia === 'CONFIDENCIAL' ? 'Alta' : 'Media';
    }

    return 'Baja';
  }

  getPeriodLabel(): string {
    return this.periodoList.find(periodo => periodo.id === this.periodoSeleccionado)?.nombre || 'Personalizado';
  }

  getCategoriaItems(): DenunciaGrafica[] {
    return (this.dashboard.porCategoria || [])
      .slice()
      .sort((a, b) => (b.total || 0) - (a.total || 0))
      .slice(0, 6);
  }

  getCategoriaPercent(item: DenunciaGrafica): number {
    const total = this.getCategoriasTotal();
    return total > 0 ? Math.round(((item.total || 0) / total) * 100) : 0;
  }

  getCategoriaBarStyle(item: DenunciaGrafica, index: number): { [key: string]: string } {
    const percent = this.getCategoriaPercent(item);
    const colors = ['#c8102e', '#d4314c', '#df5e72', '#e88a9a', '#f0b2be', '#f6d2da'];
    const color = colors[index] || colors[colors.length - 1];
    return {
      width: `${percent}%`,
      background: `linear-gradient(90deg, ${color} 0%, ${color}bf 100%)`
    };
  }

  getDonutStyle(): { [key: string]: string } {
    const recibidas = Number(this.dashboard.totalRecibidas || 0);
    const investigacion = Number(this.dashboard.totalEnInvestigacion || 0);
    const cerradas = Number(this.dashboard.totalCerradas || 0);
    const total = Math.max(recibidas + investigacion + cerradas, 1);

    const angleRecibidas = (recibidas / total) * 360;
    const angleInvestigacion = angleRecibidas + (investigacion / total) * 360;
    const gradient =
      `conic-gradient(` +
      `#c8102e 0deg ${angleRecibidas}deg, ` +
      `#1a1c1c ${angleRecibidas}deg ${angleInvestigacion}deg, ` +
      `#dfe3e8 ${angleInvestigacion}deg 360deg)`;

    return { background: gradient };
  }

  getStatusLegend(): Array<{ label: string; total: number; percent: number; className: string }> {
    const recibidas = Number(this.dashboard.totalRecibidas || 0);
    const investigacion = Number(this.dashboard.totalEnInvestigacion || 0);
    const cerradas = Number(this.dashboard.totalCerradas || 0);
    const total = Math.max(recibidas + investigacion + cerradas, 1);
    const pct = (n: number) => Math.round((n / total) * 100);
    return [
      { label: 'Recibidas', total: recibidas, percent: pct(recibidas), className: 'legend-dot--recibido' },
      { label: 'En investigación', total: investigacion, percent: pct(investigacion), className: 'legend-dot--proceso' },
      { label: 'Cerradas', total: cerradas, percent: pct(cerradas), className: 'legend-dot--cerrado' }
    ];
  }

  // ── Filtros + paginación de "Reportes Recientes" ────────────────────────────
  onFiltroTabla(): void {
    this.paginaTabla = 1;
    this.syncView();
  }

  denunciasFiltradas(): Denuncia[] {
    const id = this.fId.trim().toLowerCase();
    const cat = this.fCategoria.trim().toLowerCase();
    const fecha = this.fFecha.trim().toLowerCase();
    return this.denuncias.filter(d => {
      if (id && !('#' + (d.codigoSeguimiento || '')).toLowerCase().includes(id)) { return false; }
      if (cat && !(d.nombreCategoria || '').toLowerCase().includes(cat)) { return false; }
      if (this.fEstado != null && d.id_estatus !== this.fEstado) { return false; }
      if (this.fPrioridad && this.getPriorityLabel(d) !== this.fPrioridad) { return false; }
      if (fecha && !this.fechaTexto(d).includes(fecha)) { return false; }
      return true;
    });
  }

  private fechaTexto(d: Denuncia): string {
    if (!d.fechaRegistro) { return ''; }
    const dt = new Date(d.fechaRegistro);
    if (Number.isNaN(dt.getTime())) { return ''; }
    return dt.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' }).toLowerCase();
  }

  denunciasPagina(): Denuncia[] {
    const inicio = (this.paginaActualTabla() - 1) * this.tamPaginaTabla;
    return this.denunciasFiltradas().slice(inicio, inicio + this.tamPaginaTabla);
  }

  totalPaginasTabla(): number {
    const n = this.denunciasFiltradas().length;
    return n > 0 ? Math.ceil(n / this.tamPaginaTabla) : 1;
  }

  paginaActualTabla(): number {
    return Math.min(Math.max(this.paginaTabla, 1), this.totalPaginasTabla());
  }

  rangoInicioTabla(): number {
    return this.denunciasFiltradas().length === 0 ? 0 : (this.paginaActualTabla() - 1) * this.tamPaginaTabla + 1;
  }

  rangoFinTabla(): number {
    return Math.min(this.paginaActualTabla() * this.tamPaginaTabla, this.denunciasFiltradas().length);
  }

  irPaginaTabla(p: number): void {
    const total = this.totalPaginasTabla();
    if (p < 1 || p > total || p === this.paginaActualTabla()) { return; }
    this.paginaTabla = p;
    this.syncView();
  }

  primeraPaginaTabla(): void { this.irPaginaTabla(1); }
  paginaAnteriorTabla(): void { this.irPaginaTabla(this.paginaActualTabla() - 1); }
  paginaSiguienteTabla(): void { this.irPaginaTabla(this.paginaActualTabla() + 1); }
  ultimaPaginaTabla(): void { this.irPaginaTabla(this.totalPaginasTabla()); }

  getPaginationItemsTabla(): Array<number | string> {
    const totalPaginas = this.totalPaginasTabla();
    if (totalPaginas <= 1) { return [1]; }
    const actual = this.paginaActualTabla();
    const paginas = new Set<number>([1, totalPaginas, actual - 1, actual, actual + 1]);
    const ordenadas = Array.from(paginas).filter(p => p >= 1 && p <= totalPaginas).sort((a, b) => a - b);
    const items: Array<number | string> = [];
    for (let i = 0; i < ordenadas.length; i++) {
      const p = ordenadas[i];
      const previa = ordenadas[i - 1];
      if (previa && p - previa > 1) { items.push('e' + previa); }
      items.push(p);
    }
    return items;
  }

  isPageNumber(item: number | string): item is number {
    return typeof item === 'number';
  }

  private aplicarPeriodoSeleccionado(): void {
    if (this.periodoSeleccionado === 'all') {
      this.fechaInicio = '';
      this.fechaFin = '';
      return;
    }

    const hoy = new Date();
    const fechaFin = new Date(hoy.getFullYear(), hoy.getMonth(), hoy.getDate());
    const fechaInicio = new Date(fechaFin);
    const dias = this.periodoSeleccionado === '365' ? 364 : Number(this.periodoSeleccionado) - 1;

    fechaInicio.setDate(fechaInicio.getDate() - dias);

    this.fechaInicio = this.formatearFechaInput(fechaInicio);
    this.fechaFin = this.formatearFechaInput(fechaFin);
  }

  private formatearFechaInput(fecha: Date): string {
    const year = fecha.getFullYear();
    const month = `${fecha.getMonth() + 1}`.padStart(2, '0');
    const day = `${fecha.getDate()}`.padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  private construirFechaLocal(fecha: string, finDia: boolean): Date {
    const [year, month, day] = fecha.split('-').map(Number);
    return finDia
      ? new Date(year, month - 1, day, 23, 59, 59, 999)
      : new Date(year, month - 1, day, 0, 0, 0, 0);
  }

  private mapearFiltroEstatus(idEstatus: number): number {
    return idEstatus === EstatusDenuncia.EN_INVESTIGACION ? EstatusDenuncia.EN_INVESTIGACION : idEstatus;
  }

  private getCategoriasTotal(): number {
    const total = this.getCategoriaItems().reduce((sum, item) => sum + Number(item.total || 0), 0);
    return total || Number(this.dashboard.totalDenuncias || 0);
  }

  private syncView(): void {
    this.cdr.detectChanges();
  }
}
